import { useMemo, useRef } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { type DocumentNode, useQuery } from '@apollo/client'

import { PAGINATION } from 'Constants/constants'

import { getQueryObject } from 'Portal/src/utilities'

import { useIsOrganisation } from '../useIsOrganisation/useIsOrganisation'
import type {
  DefaultListTableDataType,
  DefaultListTableDataTypeWithEmployee,
  DefaultListTableDataTypeWithOrganisation,
  DefaultListTableMainDataType,
  DefaultListTableProps,
  DefaultListTableQueryVariables,
} from './useDefaultListTable.types'

export const useDefaultListTable = <
  OrganisationDataType extends DefaultListTableDataType,
  OrganisationDataQueryVariables extends DefaultListTableQueryVariables,
  EmployeeDataType extends DefaultListTableDataType = { employee: null },
  EmployeeDataQueryVariables extends DefaultListTableQueryVariables = {
    after: 0
    first: 0
    id: ''
  },
>(
  props: DefaultListTableProps,
) => {
  const {
    organisationQuery,
    employeeQuery,
    dataObjectPropertyName,
    extraVariables,
    isDefaultListTableInactive,
    isQueryEmployeeBased,
    customItemsPerPage,
  } = props

  const { employeeId, organisationId } = useParams<{
    employeeId?: string
    organisationId?: string
  }>()
  const { search: locationSearch } = useLocation()
  const parsedSearch = getQueryObject(locationSearch)
  const isOrganisation = useIsOrganisation()

  const { query, queryId } = useMemo((): {
    query: DocumentNode
    queryId: string
  } => {
    if (isOrganisation || !employeeQuery) {
      return {
        query: organisationQuery,
        queryId: organisationId!,
      }
    }

    return {
      query: employeeQuery,
      queryId: employeeId!,
    }
  }, [
    employeeId,
    employeeQuery,
    isOrganisation,
    organisationId,
    organisationQuery,
  ])

  const {
    data: responseData,
    error,
    fetchMore,
    loading: isLoading,
    networkStatus,
    refetch,
  } = useQuery<
    OrganisationDataType | EmployeeDataType,
    OrganisationDataQueryVariables | EmployeeDataQueryVariables
  >(query, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: isDefaultListTableInactive,
    variables: {
      after: PAGINATION.AFTER,
      first: customItemsPerPage ?? PAGINATION.ITEMS_PER_PAGE,
      id: queryId,
      searchterm: (parsedSearch?.searchTerm as string) ?? null,
      ...extraVariables,
    },
  })

  const getData = (
    data: OrganisationDataType | EmployeeDataType,
  ): DefaultListTableMainDataType | null =>
    isOrganisation && !isQueryEmployeeBased
      ? (data as DefaultListTableDataTypeWithOrganisation).organisation[
          dataObjectPropertyName
        ]
      : (data as DefaultListTableDataTypeWithEmployee).employee[
          dataObjectPropertyName
        ]

  const onLoadMoreRef = useRef<() => void>(() => {})

  onLoadMoreRef.current = () => {
    if (responseData) {
      fetchMore({
        variables: {
          after: getData(responseData)?.pageInfo.endCursor || PAGINATION.AFTER,
        },
      })
    }
  }

  const intersectionObserver = useMemo(
    () =>
      new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          onLoadMoreRef.current()
        }
      }),
    [],
  )

  const data = responseData
    ? getData(responseData)?.[dataObjectPropertyName] || []
    : []
  const totalCount = responseData ? (getData(responseData)?.totalCount ?? 0) : 0
  const hasNextPage = responseData
    ? getData(responseData)?.pageInfo.hasNextPage
    : false

  return {
    data,
    error,
    hasNextPage,
    intersectionObserver,
    isLoading,
    networkStatus,
    refetch,
    totalCount,
  }
}
